import { useEffect, useState } from 'react';
import { cryptoStore, ICalculateRes } from '@/stores/crypto.store';
import { modalStore } from '@/stores/modal.store';
import { IWallet } from '@/utils/types/IWallet';
import Loader from '@/components/Loader';
import { notify } from '@/utils/notify';
import SuccessIcon from '@/components/Notifications/SuccessIcon';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import { IDeposit } from '@/utils/types/IDeposit';

// Where the back control should return to. Restores the previous modal screen
// (e.g. the profile transactions tab, or the crypto-selection step).
interface BackTarget {
    title: string;
    component: string;
    data?: any;
}

interface Props {
    currency: string;
    value: number;
    wallet: IWallet;
    status?: IDeposit['status'];
    id?: IDeposit['id'];
    back?: BackTarget;
}

type Phase = 'idle' | 'confirming' | 'confirmed' | 'failed';

const CryptoAddress = ({ currency, value, wallet, status, id, back }: Props) => {
    const { calculate, getPrice, createDeposit, getDeposits } = cryptoStore((state) => state);
    const { setTitle, setComponent, setData } = modalStore((state) => state);

    const [amount, setAmount] = useState<ICalculateRes>();
    const [phase, setPhase] = useState<Phase>(() => {
        if (status === 'PENDING') return 'confirming';
        if (status === 'COMPLETED') return 'confirmed';
        if (status === 'DECLINED') return 'failed';
        return 'idle';
    });
    const [depositId, setDepositId] = useState<number | undefined>(id);
    const [copied, setCopied] = useState(false);

    // Opened straight from the deposit flow (no preexisting deposit).
    const isFreshFlow = !status && !id;

    // Back destination: an explicit target supplied by the opener (e.g. the
    // profile transactions tab), otherwise default to the crypto-selection step
    // for the fresh deposit flow. Undefined → nothing to go back to.
    const backTarget: BackTarget | undefined =
        back ?? (isFreshFlow ? { title: 'DEPOSIT CRYPTO', component: 'Crypto deposit' } : undefined);

    const price = getPrice(currency);

    useEffect(() => {
        let cancelled = false;
        calculate(currency, value).then((res) => {
            if (!cancelled) setAmount(res);
        });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency, value]);

    useEffect(() => {
        if (!depositId) return;
        const tick = async () => {
            const found = (await getDeposits()).find((d) => d.id === depositId);
            if (!found) return;
            if (found.status === 'PENDING') setPhase('confirming');
            if (found.status === 'COMPLETED') setPhase('confirmed');
            if (found.status === 'DECLINED') setPhase('failed');
        };
        tick();
        const iv = window.setInterval(tick, 30000);
        return () => window.clearInterval(iv);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [depositId]);

    function goBack() {
        if (!backTarget) return;
        setTitle(backTarget.title);
        if (backTarget.data !== undefined) setData(backTarget.data);
        setComponent(backTarget.component);
    }

    async function copyAddress() {
        try {
            await navigator.clipboard.writeText(wallet.wallet);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1400);
        } catch (e) {
            notify('error', <AlertIcon />, <NotifyWrapper title="Error" text={String(e)} />);
        }
    }

    async function pay() {
        if (!amount) return;
        const deposit = await createDeposit(wallet.id, amount.amountUsd);
        if (!deposit) {
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper
                    title="Error"
                    text="Something went wrong, please try a different number"
                />
            );
            return;
        }
        setDepositId(deposit.id);
        setPhase('confirming');
        notify(
            'success',
            <SuccessIcon />,
            <NotifyWrapper title="Confirming" text="Watching the blockchain…" />
        );
    }

    if (!amount) {
        return (
            <div className="w-80 h-60 flex items-center justify-center">
                <Loader />
            </div>
        );
    }

    const overlayActive = phase === 'confirming' || phase === 'confirmed' || phase === 'failed';
    const statusColor =
        phase === 'confirmed' ? '#46e08a' : phase === 'failed' ? '#ff6262' : '#3ad6ff';
    const formattedCoin =
        typeof amount.amountCoin === 'number' ? amount.amountCoin.toString() : '—';

    return (
        <div className="relative flex flex-col gap-4 min-w-[420px] max-md:min-w-0">
            {/* Back to the previous screen (crypto selection / profile) */}
            {backTarget && (
                <button
                    onClick={goBack}
                    className="group/back self-start inline-flex items-center gap-1.5 font-chat-nick text-[11px] font-extrabold tracking-[0.12em] uppercase text-text_3 hover:text-white px-2.5 py-1.5 -ml-1 rounded-fl border border-transparent hover:border-stroke_strong hover:bg-white/[0.04] transition-all"
                >
                    <svg
                        viewBox="0 0 24 24"
                        className="w-3.5 h-3.5 stroke-current fill-none [stroke-width:2.6] [stroke-linecap:round] [stroke-linejoin:round] -translate-x-0 group-hover/back:-translate-x-0.5 transition-transform"
                    >
                        <path d="M15 6l-6 6 6 6" />
                    </svg>
                    Back
                </button>
            )}

            {/* Rate pill */}
            <span className="font-chat-nick inline-flex items-center gap-2 px-3.5 py-2 rounded-fl bg-white/[0.03] border border-stroke text-[13px] font-bold text-text_2 w-fit">
                1 {currency} <span className="text-text_3 font-normal">=</span>
                <b
                    className="font-extrabold tabular-nums"
                    style={{ color: '#5fe0ff' }}
                >
                    ${price}
                </b>
            </span>

            {/* Amount to pay */}
            <div className="flex items-baseline gap-2.5">
                <span
                    className="font-chat-nick text-[32px] font-extrabold text-white tabular-nums"
                    style={{
                        letterSpacing: '-0.02em',
                        textShadow: '0 0 22px rgba(139,109,255,0.35)',
                    }}
                >
                    {formattedCoin}
                </span>
                <span className="text-base text-text_3 font-semibold">(${value})</span>
            </div>

            {/* Deposit address */}
            <div>
                <div className="font-chat-nick text-[11px] uppercase tracking-[0.14em] text-text_3 font-bold mb-2">
                    Deposit address ({currency} only)
                </div>
                <div className="flex items-stretch bg-white/[0.03] border border-stroke rounded-fl overflow-hidden hover:border-stroke_strong transition-colors focus-within:border-[#8b6dff]/55 focus-within:shadow-[0_0_22px_-8px_rgba(139,109,255,0.7)]">
                    <input
                        readOnly
                        value={wallet.wallet}
                        className="flex-1 min-w-0 bg-transparent border-0 outline-none text-white px-4 py-3 text-sm tracking-[0.02em] font-mono"
                    />
                    <button
                        onClick={copyAddress}
                        aria-label="Copy"
                        className={`w-11 grid place-items-center transition-colors border-l border-stroke ${
                            copied
                                ? 'text-[#7ff0b5] bg-[rgba(70,224,138,0.10)]'
                                : 'text-text_3 hover:text-white hover:bg-[rgba(139,109,255,0.12)]'
                        }`}
                    >
                        {copied ? (
                            <svg
                                viewBox="0 0 24 24"
                                className="w-4 h-4 stroke-current fill-none [stroke-width:2.5] [stroke-linecap:round] [stroke-linejoin:round]"
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        ) : (
                            <svg
                                viewBox="0 0 24 24"
                                className="w-4 h-4 stroke-current fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]"
                            >
                                <rect x="9" y="9" width="13" height="13" rx="2" />
                                <path d="M5 15V5a2 2 0 0 1 2-2h10" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {/* Idle: instructions + pay action */}
            {!overlayActive && (
                <>
                    <div className="font-chat-msg text-[13px] text-text_3 leading-relaxed">
                        Pay for{' '}
                        <span className="text-white font-bold tabular-nums">
                            {formattedCoin}
                            {currency}
                        </span>{' '}
                        and click the{' '}
                        <span className="font-extrabold" style={{ color: '#5fe0ff' }}>
                            I PAID
                        </span>{' '}
                        button
                    </div>

                    {/* Primary CTA — brand shine */}
                    <button
                        onClick={pay}
                        className="group relative overflow-hidden w-full py-3.5 mt-1 rounded-fl font-chat-nick text-[13px] font-extrabold tracking-[0.08em] text-white hover:-translate-y-0.5 transition-transform"
                        style={{
                            background: 'linear-gradient(135deg, #8b6dff, #3ad6ff)',
                            boxShadow:
                                'inset 0 1px 0 rgba(255,255,255,0.35), 0 0 22px -4px rgba(139,109,255,0.7), 0 8px 20px -6px rgba(58,214,255,0.5)',
                        }}
                    >
                        <span
                            aria-hidden
                            className="pointer-events-none absolute inset-0 -translate-x-[120%] group-hover:translate-x-[120%] transition-transform duration-[700ms] ease-out"
                            style={{
                                background:
                                    'linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.5), transparent 70%)',
                            }}
                        />
                        <span className="relative">I PAID</span>
                    </button>
                </>
            )}

            {/* Active deposit — status shown inline as a card (not an overlay) */}
            {overlayActive && (
                <div className="flex flex-col gap-3">
                    <div
                        className="flex items-center gap-4 p-4 rounded-fl-md border"
                        style={{
                            borderColor: `color-mix(in srgb, ${statusColor} 38%, transparent)`,
                            background: `color-mix(in srgb, ${statusColor} 9%, transparent)`,
                            animation: 'pfRowIn 0.4s cubic-bezier(0.18,0.84,0.30,1) both',
                        }}
                    >
                        {phase === 'confirming' && (
                            <div
                                className="w-12 h-12 rounded-full border-4 shrink-0"
                                style={{
                                    borderColor: 'rgba(255,255,255,0.10)',
                                    borderTopColor: statusColor,
                                    boxShadow: `0 0 22px -6px ${statusColor}`,
                                    animation: 'confirmSpin 0.9s linear infinite',
                                }}
                            />
                        )}
                        {phase === 'confirmed' && (
                            <div
                                className="w-12 h-12 rounded-full grid place-items-center text-white shrink-0"
                                style={{
                                    background: 'linear-gradient(135deg, #46e08a, #2bb89b)',
                                    boxShadow: '0 0 26px -4px rgba(70,224,138,0.6)',
                                    animation: 'confirmPop 0.55s cubic-bezier(0.34,1.56,0.64,1)',
                                }}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    className="w-6 h-6 stroke-current fill-none [stroke-width:3] [stroke-linecap:round] [stroke-linejoin:round]"
                                >
                                    <path d="M5 12l4 4 10-10" />
                                </svg>
                            </div>
                        )}
                        {phase === 'failed' && (
                            <div
                                className="w-12 h-12 rounded-full grid place-items-center text-white shrink-0"
                                style={{
                                    background: 'linear-gradient(135deg, #ff6262, #f04e3a)',
                                    boxShadow: '0 0 26px -4px rgba(255,98,98,0.55)',
                                    animation: 'confirmPop 0.55s cubic-bezier(0.34,1.56,0.64,1)',
                                }}
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    className="w-6 h-6 stroke-current fill-none [stroke-width:3] [stroke-linecap:round]"
                                >
                                    <path d="M6 6l12 12M18 6L6 18" />
                                </svg>
                            </div>
                        )}
                        <div className="flex flex-col gap-0.5 min-w-0">
                            <div
                                className="font-chat-nick text-[15px] font-extrabold tracking-[0.02em]"
                                style={{
                                    color:
                                        phase === 'confirmed'
                                            ? '#7ff0b5'
                                            : phase === 'failed'
                                              ? '#ff6262'
                                              : '#f5f6f8',
                                }}
                            >
                                {phase === 'confirming' && 'Confirming on the network…'}
                                {phase === 'confirmed' && 'Deposit confirmed'}
                                {phase === 'failed' && 'Transaction declined'}
                            </div>
                            <div className="font-chat-msg text-[12.5px] text-text_3">
                                {phase === 'confirming' && 'This usually takes a few seconds'}
                                {phase === 'confirmed' && 'Funds added to your balance'}
                                {phase === 'failed' && 'Try again with a different amount'}
                            </div>
                        </div>
                    </div>

                    {phase === 'failed' && backTarget && (
                        <button
                            onClick={goBack}
                            className="w-full py-3 rounded-fl font-chat-nick text-[12px] font-extrabold tracking-[0.08em] uppercase text-white border border-stroke_strong bg-white/[0.04] hover:bg-white/[0.08] hover:-translate-y-0.5 transition-all"
                        >
                            {isFreshFlow ? 'Try again' : 'Back'}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default CryptoAddress;
